import { useCallback, useMemo, useState } from "react";
import { FlatList, Pressable, type LayoutChangeEvent } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import type { WorklistScenarioRow, WorklistSiteGroup } from "../utils/worklist";
import { ScenarioStatusPill, ScenarioStalePill, ScenarioExcludedPill } from "./ScenarioStatusPill";

// Below this row width the status pill + action button flow onto a second line
// beneath the text (mirrors the configure-page input reflow). Above it, they sit
// inline to the right. Measured per-row because rows live inside WebLayout beside
// a collapsible sidebar, so window breakpoints would be wrong.
const STACK_WIDTH = 360;

/** Measure a row's own width and report whether it should stack (narrow). */
function useRowStack(): { stacked: boolean; onLayout: (e: LayoutChangeEvent) => void } {
  const [width, setWidth] = useState(0);
  const onLayout = useCallback((e: LayoutChangeEvent) => {
    setWidth(e.nativeEvent.layout.width);
  }, []);
  // 0 = not yet measured → assume wide (avoids a stacked flash on first paint).
  return { stacked: width > 0 && width < STACK_WIDTH, onLayout };
}

interface WorklistProps {
  groups: WorklistSiteGroup[];
  /** Navigate to the config/map page for a whole site (no scenario selected). */
  onViewSiteOnMap: (siteId: string) => void;
  /** Navigate to the config/map page with a specific scenario selected. */
  onConfigureScenario: (row: WorklistScenarioRow) => void;
}

type ListItem =
  | { type: "site"; key: string; group: WorklistSiteGroup }
  | { type: "scenario"; key: string; row: WorklistScenarioRow };

/**
 * Text-only operator worklist: sites in first-appearance order, each with its
 * scenario rows directly beneath. Deliberately no map here (the map moved to the
 * config page) and no scenario data/history — this is a fast triage list whose
 * only job is to show what's missing and let the operator jump to configure it.
 * A single flattened FlatList keeps long tenant lists cheap to render.
 */
export function Worklist({ groups, onViewSiteOnMap, onConfigureScenario }: WorklistProps) {
  const items = useMemo((): ListItem[] => {
    return groups.flatMap((group) => [
      { type: "site" as const, key: `site-${group.siteId}`, group },
      ...group.rows.map((row) => ({ type: "scenario" as const, key: row.key, row })),
    ]);
  }, [groups]);

  const renderItem = useCallback(
    ({ item }: { item: ListItem }) => {
      if (item.type === "site") {
        return <SiteHeader group={item.group} onViewOnMap={() => onViewSiteOnMap(item.group.siteId)} />;
      }
      return <ScenarioRow row={item.row} onConfigure={() => onConfigureScenario(item.row)} />;
    },
    [onConfigureScenario, onViewSiteOnMap],
  );

  return (
    <FlatList
      data={items}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      contentContainerStyle={styles.listContent}
      showsVerticalScrollIndicator={false}
      windowSize={8}
      initialNumToRender={16}
      maxToRenderPerBatch={16}
    />
  );
}

const keyExtractor = (item: ListItem) => item.key;

interface SiteHeaderProps {
  group: WorklistSiteGroup;
  onViewOnMap: () => void;
}

function SiteHeader({ group, onViewOnMap }: SiteHeaderProps) {
  const { stacked, onLayout } = useRowStack();
  return (
    <View style={[styles.siteHeader, stacked && styles.rowStacked]} onLayout={onLayout}>
      <View style={[styles.siteHeaderText, stacked && styles.textStacked]}>
        <Text textStyle="md/semibold" truncate>
          {group.siteName}
        </Text>
        {group.address ?
          <Text textStyle="xs/regular" color="textSecondary" truncate>
            {group.address}
          </Text>
        : null}
        <Text textStyle="xs/medium" color="textSecondary">
          {group.missingCount} missing · {group.configuredCount} configured · {group.totalCount} total
        </Text>
      </View>
      {/* When stacked, the actions group sits left-aligned on its own line. */}
      <View style={styles.actionsGroup}>
        <Pressable
          onPress={onViewOnMap}
          accessibilityRole="button"
          accessibilityLabel={`View ${group.siteName} on map`}
          style={styles.action}
        >
          <Icon name="map" size={16} />
          <Text textStyle="xs/semibold">View on map</Text>
        </Pressable>
      </View>
    </View>
  );
}

interface ScenarioRowProps {
  row: WorklistScenarioRow;
  onConfigure: () => void;
}

function ScenarioRow({ row, onConfigure }: ScenarioRowProps) {
  const { stacked, onLayout } = useRowStack();
  return (
    <View style={[styles.scenarioRow, stacked && styles.rowStacked]} onLayout={onLayout}>
      <View style={[styles.scenarioText, stacked && styles.textStacked]}>
        <Text textStyle="sm/medium" truncate>
          {row.scenarioName}
        </Text>
        <Text textStyle="xs/regular" color="textSecondary" truncate>
          {row.assetCameraLabel}
        </Text>
      </View>
      {/* Pill + Configure stay side-by-side; when the row stacks they drop to a
          second line left-aligned beneath the text. */}
      <View style={styles.actionsGroup}>
        <ScenarioStatusPill status={row.status} />
        {row.stale ?
          <ScenarioStalePill />
        : null}
        {row.excluded ?
          <ScenarioExcludedPill />
        : null}
        <Pressable
          onPress={onConfigure}
          accessibilityRole="button"
          accessibilityLabel={`Configure ${row.scenarioName}`}
          style={styles.action}
        >
          <Icon name="settings" size={16} />
          <Text textStyle="xs/semibold">Configure</Text>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  listContent: {
    flexGrow: 1,
    gap: theme.spacing.sm,
    paddingBottom: theme.spacing["7xl"],
  },
  siteHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: theme.spacing.xl,
    paddingTop: theme.spacing["4xl"],
    paddingBottom: theme.spacing.md,
  },
  siteHeaderText: {
    flex: 1,
    gap: theme.spacing.xxs,
    minWidth: 0,
  },
  scenarioRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.xl,
    paddingVertical: theme.spacing.lg,
    paddingHorizontal: theme.spacing["3xl"],
    borderRadius: theme.spacing.xl,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    backgroundColor: theme.colors.surfaceCard,
  },
  // Narrow layout (< STACK_WIDTH): text on top, actions group on the line below,
  // both left-aligned. Applied on top of siteHeader/scenarioRow so only the
  // axis + alignment change.
  rowStacked: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing.md,
  },
  // When stacked, the text block fills the row width (rather than shrinking to
  // content in the column), so long names/addresses still truncate correctly.
  textStacked: {
    alignSelf: "stretch",
    width: "100%",
  },
  scenarioText: {
    flex: 1,
    gap: theme.spacing.xxs,
    minWidth: 0,
  },
  // Keeps the pill + button side-by-side whether inline (wide) or stacked
  // (narrow). flexShrink:0 stops the button text wrapping when space is tight.
  // flexWrap lets the extra stale/excluded pills reflow beneath (right-aligned)
  // on medium widths above the stack breakpoint, rather than overflowing the row
  // or crushing the scenario name — matches the ConfigScenarioList pill group.
  actionsGroup: {
    flexDirection: "row",
    alignItems: "center",
    flexShrink: 0,
    flexWrap: "wrap",
    justifyContent: "flex-end",
    gap: theme.spacing.md,
  },
  action: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.xs,
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.xl,
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
  },
}));
